import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import 'quill/dist/quill.snow.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string | number;
  readOnly?: boolean;
  enableWorkDescriptionTemplate?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter your notes...',
  height = '200px',
  readOnly = false,
  enableWorkDescriptionTemplate = false,
}) => {
  const theme = useTheme();
  const editorRef = useRef<HTMLDivElement>(null);
  const quillInstanceRef = useRef<any>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const isInternalUpdateRef = useRef<boolean>(false);
  const lastValueRef = useRef<string>(value);
  // Default template for work description
  const getDefaultWorkDescriptionTemplate = (): string => {
    return `
      <p><strong>We include installation of the following:</strong></p>
      <ol>
        <li>Inclusion 1</li>
        <li>Inclusion 2</li>
      </ol>
      <p><strong>We exclude the following:</strong></p>
      <ol>
        <li>Exclusion 1</li>
        <li>Exclusion 2</li>
      </ol>
      <p><strong>The following are included as alternates (VE):</strong></p>
      <ol>
        <li>Alternate 1</li>
        <li>Alternate 2</li>
      </ol>
    `;
  };

  // Helper function to get content with default template fallback
  const getContentWithTemplate = useCallback(
    (content: string): string => {
      // Only apply template if enableWorkDescriptionTemplate is true
      if (!enableWorkDescriptionTemplate) {
        return content || '';
      }

      // Check for empty content (null, undefined, empty string, or only whitespace)
      const hasNoContent =
        !content ||
        content === '' ||
        (typeof content === 'string' && content.trim() === '') ||
        (typeof content === 'string' && content.replace(/\s+/g, '') === '');

      if (hasNoContent) {
        return getDefaultWorkDescriptionTemplate();
      }

      return content;
    },
    [enableWorkDescriptionTemplate]
  );

  // Simple toolbar configuration - memoized to prevent recreation
  const toolbarConfig = useMemo(
    () => [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['link'],
      ['clean'],
    ],
    []
  );

  const formats = useMemo(
    () => ['header', 'bold', 'italic', 'underline', 'list', 'align', 'link', 'indent'],
    []
  );

  // Stable change handler
  const handleChange = useCallback(
    (content: string) => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Only call onChange if it's not an internal update
      if (!isInternalUpdateRef.current) {
        lastValueRef.current = content;
        onChange(content);
      }
    },
    [onChange]
  );

  // Update editor content when external value changes
  useEffect(() => {
    if (!isInternalUpdateRef.current && value !== lastValueRef.current) {
      lastValueRef.current = value;

      if (quillInstanceRef.current) {
        // Quill is ready, set content immediately
        const quill = quillInstanceRef.current;

        isInternalUpdateRef.current = true;

        const contentToSet = getContentWithTemplate(value || '');

        const delta = quill.clipboard.convert({ html: contentToSet });
        quill.setContents(delta, 'silent');

        isInternalUpdateRef.current = false;
      }
    }
  }, [value, getContentWithTemplate]);

  // Initialize Quill editor once and keep it stable
  useEffect(() => {
    const initializeEditor = async () => {
      if (!editorRef.current || quillInstanceRef.current) return;

      try {
        // Dynamic import to avoid SSR issues and ensure latest version
        const { default: Quill } = await import('quill');

        // Create editor container
        const editorContainer = document.createElement('div');
        editorRef.current.appendChild(editorContainer);

        // Initialize Quill with modern configuration
        const quillInstance = new Quill(editorContainer, {
          theme: 'snow',
          readOnly,
          modules: {
            toolbar: toolbarConfig,
            clipboard: {
              matchVisual: false,
            },
            history: {
              delay: 1000,
              maxStack: 50,
              userOnly: true,
            },
          },
          formats,
          placeholder,
        });

        // Store the instance
        quillInstanceRef.current = quillInstance;

        // Set initial content using the current value (with template fallback for empty content)
        const currentValue = lastValueRef.current || value || '';
        const initialContent = getContentWithTemplate(currentValue);
        const delta = quillInstance.clipboard.convert({ html: initialContent });
        quillInstance.setContents(delta, 'silent');
        lastValueRef.current = currentValue;

        // Only force refresh for work description templates to ensure template is applied
        // For other content types, avoid overriding with potentially stale values
        if (enableWorkDescriptionTemplate) {
          setTimeout(() => {
            if (quillInstance) {
              const currentValue = lastValueRef.current || value || '';
              const refreshContent = getContentWithTemplate(currentValue);
              const refreshDelta = quillInstance.clipboard.convert({ html: refreshContent });
              quillInstance.setContents(refreshDelta, 'silent');
            }
          }, 50);
        }

        // Handle text changes
        quillInstance.on('text-change', (delta: any, oldDelta: any, source: string) => {
          if (source === 'user') {
            const html = quillInstance.getSemanticHTML();
            handleChange(html);
          }
        });
      } catch (error) {
        console.error('Failed to initialize Quill editor:', error);
        // Fallback to textarea if Quill fails to load
        if (editorRef.current) {
          const textarea = document.createElement('textarea');
          // Use template fallback for textarea as well, but strip HTML tags for plain text
          const currentValue = lastValueRef.current || value || '';
          const fallbackContent = getContentWithTemplate(currentValue);
          textarea.value = fallbackContent.replace(/<[^>]*>/g, ''); // Strip HTML for textarea
          textarea.placeholder = placeholder;
          textarea.readOnly = readOnly;
          textarea.style.width = '100%';
          textarea.style.height = typeof height === 'string' ? height : `${height}px`;
          textarea.style.border = `1px solid ${theme.palette.divider}`;
          textarea.style.borderRadius = '4px';
          textarea.style.padding = '12px';
          textarea.style.fontFamily = theme.typography.fontFamily || 'inherit';
          textarea.style.fontSize = '14px';
          textarea.style.color = theme.palette.text.primary;
          textarea.style.backgroundColor = theme.palette.background.paper;
          textarea.style.resize = 'vertical';
          textarea.style.wordWrap = 'break-word';
          textarea.style.overflowWrap = 'break-word';
          textarea.style.wordBreak = 'normal';
          textarea.style.whiteSpace = 'normal';

          textarea.addEventListener('input', e => {
            const target = e.target as HTMLTextAreaElement;
            handleChange(target.value);
          });

          editorRef.current.appendChild(textarea);
        }
      }
    };

    initializeEditor();

    // Capture the current ref values for cleanup
    const currentEditorRef = editorRef.current;
    const currentQuillInstance = quillInstanceRef.current;
    const currentTimeout = timeoutRef.current;

    // Cleanup function
    return () => {
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
      if (currentQuillInstance) {
        quillInstanceRef.current = null;
      }
      if (currentEditorRef) {
        currentEditorRef.innerHTML = '';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update readOnly state when it changes
  useEffect(() => {
    if (quillInstanceRef.current) {
      quillInstanceRef.current.enable(!readOnly);
    }
  }, [readOnly]);

  return (
    <Box
      sx={{
        height: typeof height === 'string' ? height : `${height}px`,
        display: 'flex',
        flexDirection: 'column',
        '& .ql-toolbar': {
          borderTop: `1px solid ${theme.palette.divider}`,
          borderLeft: `1px solid ${theme.palette.divider}`,
          borderRight: `1px solid ${theme.palette.divider}`,
          borderBottom: 'none',
          backgroundColor: theme.palette.mode === 'dark' ? '#2c2c2c' : '#fafafa',
          borderRadius: '4px 4px 0 0',
          padding: '12px',
          '& .ql-formats': {
            marginRight: '15px',
          },
        },
        '& .ql-toolbar .ql-stroke': {
          stroke: theme.palette.text.primary,
        },
        '& .ql-toolbar .ql-fill': {
          fill: theme.palette.text.primary,
        },
        '& .ql-toolbar .ql-picker-label': {
          color: theme.palette.text.primary,
          padding: '2px 8px',
        },
        '& .ql-container': {
          borderBottom: `1px solid ${theme.palette.divider}`,
          borderLeft: `1px solid ${theme.palette.divider}`,
          borderRight: `1px solid ${theme.palette.divider}`,
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          backgroundColor: theme.palette.background.paper,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Prevent container scroll
        },
        '& .ql-editor': {
          flex: 1,
          color: theme.palette.text.primary,
          fontSize: '15px',
          fontFamily: theme.typography.fontFamily,
          padding: '24px 32px',
          minHeight: '100px',
          lineHeight: 1.6,
          overflowY: 'auto', // Enable scroll on editor
          overflowX: 'hidden', // Prevent horizontal overflow
          maxWidth: '100%',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          wordBreak: 'normal',
          whiteSpace: 'normal',
          '&:focus': {
            outline: 'none',
          },
          // Enhanced typography styles
          '& p': {
            marginBottom: '1em',
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'normal',
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            marginTop: '1.5em',
            marginBottom: '0.75em',
            lineHeight: 1.3,
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'normal',
          },
          '& h1': { fontSize: '2em' },
          '& h2': { fontSize: '1.75em' },
          '& h3': { fontSize: '1.5em' },
          '& h4': { fontSize: '1.25em' },
          '& ul, & ol': {
            paddingLeft: '2em',
            marginBottom: '1em',
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'normal',
          },
          '& li': {
            marginBottom: '0.5em',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'normal',
          },
          '& strong': {
            fontWeight: 600,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'normal',
          },
          '& blockquote': {
            borderLeft: `4px solid ${theme.palette.divider}`,
            paddingLeft: '16px',
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: '1200px',
            fontStyle: 'italic',
          },
          // Customize scrollbar
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' ? '#555' : '#888',
            borderRadius: '4px',
            '&:hover': {
              background: theme.palette.mode === 'dark' ? '#666' : '#999',
            },
          },
        },
        '& .ql-editor.ql-blank::before': {
          color: theme.palette.text.secondary,
          fontStyle: 'normal',
          opacity: 0.6,
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto',
        },
        // Global word break prevention for all Quill content
        '& .ql-editor *': {
          wordWrap: 'break-word !important',
          overflowWrap: 'break-word !important',
          wordBreak: 'normal !important',
        },
      }}
      onKeyDown={e => {
        e.stopPropagation();
      }}
    >
      <div
        ref={editorRef}
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        onKeyDown={e => {
          e.stopPropagation();
        }}
      />
    </Box>
  );
};

export default RichTextEditor;
